/**
 * Instamart-style Quick View — final mobile bottom-sheet controller.
 *
 * Interaction model:
 * - Tap a product card to open the collapsed quick view.
 * - Swipe vertically to expand, collapse, or dismiss the sheet.
 * - Swipe horizontally on the HERO IMAGE to change that product's photographs.
 * - Use the circular product dock, or swipe the non-interactive preview area,
 *   to move between products from the same collection/grid.
 * - Product data comes from embedded JSON or Shopify's product .js endpoint.
 *
 * The script intentionally avoids forcing layout dimensions. The CSS controls
 * visual sizing; JavaScript only measures the rendered content to calculate
 * reliable collapsed and expanded detents.
 */
(function () {
  'use strict';

  // Shape-check (an element with id="InstamartQuickView*" would also make this
  // truthy), so the re-init guard can't be tripped by DOM clobbering.
  if (window.InstamartQuickView && typeof window.InstamartQuickView.open === 'function') return;

  var config = window.iqvConfig || {};
  var routes = config.routes || {};
  var strings = config.strings || {};
  var fallback = config.fallback || {};

  var CART_ADD_URL = routes.cartAddUrl || '/cart/add.js';
  var CART_URL = routes.cartUrl || '/cart.js';
  var MONEY_FORMAT = config.moneyFormat || '₹{{amount}}';
  var ADDED_RESET_MS = 1800;

  var DEFAULT_AUTO_BIND_SELECTOR = [
    '.grid-view-item .gimg-link',
    '.grid-view-item .grid-view-item__title',
    '.grid-view-item .overly',
    '.grid-view-item__link',
    '.list-view-item__image-wrapper a',
    '.list-view-item__title',
  ].join(', ');

  var cache = new Map(); // normalized product data by url key
  var detailCache = new Map(); // injected PDP html by url key
  var els = {};
  var state = {
    product: null,
    activeVariantId: null,
    lastFocused: null,
    session: 0,
    scrollY: 0,
    siblings: [],
    index: -1,
    expanded: false,
    activeImageIndex: 0,
    detents: { collapsedH: 0, expandedH: 0, dockGap: 16 },
  };

  ready(init);

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  function init() {
    var overlay = document.querySelector('[data-iqv-overlay]');
    if (!overlay) return;
    cacheEls(overlay);
    bindEvents();
    if (els.wishlistBtn && !config.enableWishlist) els.wishlistBtn.hidden = true;

    window.InstamartQuickView = {
      open: openFromUrl,
      openProduct: openFromTrigger,
      close: close,
      expand: doExpand,
      collapse: doCollapse
    };
  }

  // --------------------------------------------------------------------
  function cacheEls(overlay) {
    els.overlay = overlay;
    els.modal = overlay.querySelector('[data-iqv-modal]');
    els.sheet = overlay.querySelector('[data-iqv-sheet]');
    els.grabber = overlay.querySelector('[data-iqv-grabber]');
    els.scroll = overlay.querySelector('[data-iqv-scroll]');
    els.pager = overlay.querySelector('[data-iqv-pager]');
    els.preview = overlay.querySelector('.iqv-preview');
    els.hero = overlay.querySelector('[data-iqv-hero]');
    els.heroTrack = overlay.querySelector('[data-iqv-hero-track]');
    els.dots = overlay.querySelector('[data-iqv-dots]');
    els.rating = overlay.querySelector('[data-iqv-rating]');
    els.reviewEyebrow = overlay.querySelector('.iqv-review-summary__eyebrow');
    els.reviewLabel = overlay.querySelector('.iqv-review-summary__label');
    els.reviewArrow = overlay.querySelector('.iqv-review-summary__arrow');
    els.wishlistBtn = overlay.querySelector('[data-iqv-wishlist]');
    els.shareBtn = overlay.querySelector('[data-iqv-share]');
    els.explore = overlay.querySelector('[data-iqv-explore]');
    els.exploreText = overlay.querySelector('[data-iqv-explore-text]');
    els.title = overlay.querySelector('[data-iqv-title]');
    els.subtitle = overlay.querySelector('[data-iqv-subtitle]');
    els.qty = overlay.querySelector('[data-iqv-qty]');
    els.variantsWrap = overlay.querySelector('[data-iqv-variants-wrap]');
    els.variantsRail = overlay.querySelector('[data-iqv-variants-rail]');
    els.discount = overlay.querySelector('[data-iqv-discount]');
    els.price = overlay.querySelector('[data-iqv-price]');
    els.comparePrice = overlay.querySelector('[data-iqv-compare-price]');
    els.unitPrice = overlay.querySelector('[data-iqv-unit-price]');
    els.offerCard = overlay.querySelector('[data-iqv-offer-card]');
    els.offer = overlay.querySelector('[data-iqv-offer]');
    els.buybar = overlay.querySelector('[data-iqv-buybar]');
    els.addBtn = overlay.querySelector('[data-iqv-add]');
    els.addLabel = overlay.querySelector('[data-iqv-add-label]');
    els.dock = overlay.querySelector('[data-iqv-dock]');
    els.thumbRail = overlay.querySelector('[data-iqv-thumb-rail]');
    els.detail = overlay.querySelector('[data-iqv-detail]');
    els.detailContent = overlay.querySelector('[data-iqv-detail-content]');
    els.detailLoading = overlay.querySelector('[data-iqv-detail-loading]');
    els.retryBtn = overlay.querySelector('[data-iqv-retry]');
  }

  function bindEvents() {
    document.addEventListener('click', onDocumentClick);
    document.addEventListener('keydown', onDocumentKeydown);
    els.overlay.querySelectorAll('[data-iqv-close]').forEach(function (b) {
      b.addEventListener('click', close);
    });
    if (els.addBtn) els.addBtn.addEventListener('click', onAddClick);
    if (els.shareBtn) els.shareBtn.addEventListener('click', onShareClick);
    if (els.retryBtn) {
      els.retryBtn.addEventListener('click', function () {
        if (state.product) reopen(state.product);
      });
    }
    bindGestures();
    window.addEventListener('resize', debounce(function () {
      if (!els.overlay.classList.contains('is-open')) return;
      if (!isMobile()) { close(); return; } // grew to desktop → dismiss
      computeDetents();
      snapTo(state.expanded ? 'expanded' : 'collapsed', false);
    }, 200));
  }

  // This is a MOBILE-only experience (an app-style bottom sheet). On desktop
  // we don't intercept anything — product links navigate to the real product
  // page as usual. Tablets/phones (< 990px, the theme's mobile breakpoint) get
  // the quick view.
  function isMobile() {
    return window.matchMedia('(max-width: 989px)').matches;
  }

  function onDocumentClick(e) {
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    if (!isMobile()) return; // desktop: let the click navigate normally
    if (e.target.closest('[data-iqv-ignore]')) return;
    if (e.target.closest('button, input, select, textarea') && !e.target.closest('[data-iqv-trigger]')) return;
    var trigger = e.target.closest('[data-iqv-trigger]');
    if (trigger) {
      if (trigger.closest('[data-iqv-ignore]')) return;
      e.preventDefault();
      openFromTrigger(trigger);
      return;
    }
    var selector = getAutoBindSelector();
    var link = selector && e.target.closest(selector);
    if (!link || link.closest('[data-iqv-ignore]') || link.target === '_blank') return;
    var href = link.getAttribute('href');
    if (!href || href.charAt(0) === '#') return;
    e.preventDefault();
    openFromUrl(href, link);
  }

  function getAutoBindSelector() {
    if (Array.isArray(config.autoBindSelectors)) return config.autoBindSelectors.length ? config.autoBindSelectors.join(', ') : null;
    if (typeof config.autoBindSelectors === 'string') return config.autoBindSelectors;
    return DEFAULT_AUTO_BIND_SELECTOR;
  }

  function onDocumentKeydown(e) {
    if (e.key === 'Escape' && els.overlay.classList.contains('is-open')) close();
  }

  // --------------------------------------------------------------------
  // Opening
  // --------------------------------------------------------------------
  function openFromTrigger(triggerEl) {
    var data = readEmbeddedProduct(triggerEl);
    if (!data) return;
    cache.set(cleanUrl(data.url), data);
    state.siblings = buildSiblings(triggerEl);
    state.index = indexOfHandle(state.siblings, urlHandle(data.url));
    reopen(data, triggerEl);
  }

  function openFromUrl(href, triggerEl) {
    state.siblings = buildSiblings(triggerEl);
    state.index = indexOfHandle(state.siblings, urlHandle(href));
    var key = cleanUrl(href);
    openShell(triggerEl);
    state.session++;
    var mySession = state.session;
    var cached = cache.get(key);
    if (cached) return finishOpen(cached);
    setLoading(true);
    fetch(productJsonUrl(key), { credentials: 'same-origin' })
      .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
      .then(function (raw) {
        var data = normalizeAjax(raw);
        cache.set(key, data);
        if (state.session === mySession) finishOpen(data);
      })
      .catch(function (err) { console.error('[IQV]', err); if (state.session === mySession) setError(true); });
  }

  function reopen(data, triggerEl) {
    openShell(triggerEl);
    state.session++;
    finishOpen(data);
  }

 function finishOpen(data) {
  fillContent(data);
  setLoading(false);
  setError(false);

  computeDetents();
  snapTo(state.expanded ? 'expanded' : 'collapsed', true);

  /*
   * Recalculate after the browser finishes laying out the product title,
   * image and fonts. This prevents the price from being pushed below the
   * collapsed popup after a long title wraps.
   */
  requestAnimationFrame(function () {
    if (!els.overlay.classList.contains('is-open')) return;

    computeDetents();
    snapTo(state.expanded ? 'expanded' : 'collapsed', false);
  });

  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(function () {
      if (!els.overlay.classList.contains('is-open')) return;

      computeDetents();
      snapTo(state.expanded ? 'expanded' : 'collapsed', false);
    });
  }
}

  function readEmbeddedProduct(triggerEl) {
    var id = triggerEl.getAttribute('data-iqv-json');
    var script = id && document.getElementById(id);
    if (!script) { console.error('[IQV] no product JSON for trigger'); return null; }
    try {
      var raw = JSON.parse(script.textContent);
      if (!raw || typeof raw !== 'object') return null;
      return normalizeEmbedded(raw);
    } catch (e) { console.error('[IQV] bad JSON', e); return null; }
  }

  // --------------------------------------------------------------------
  // Shell — open/close, scroll lock, detents
  // --------------------------------------------------------------------
  function openShell(triggerEl) {
    state.lastFocused = triggerEl || state.lastFocused || document.activeElement;
    if (els.overlay.classList.contains('is-open')) return;
    lockBodyScroll();
    setError(false);
    state.expanded = false;
    els.sheet.classList.add('is-collapsed');
    els.sheet.classList.remove('is-expanded');
    els.overlay.classList.add('is-open');
    els.overlay.setAttribute('aria-hidden', 'false');
    activateFocusTrap();
    // Provisional detent (real one is set by finishOpen once content is in).
    state.detents.collapsedH = Math.round(getVH() * 0.66);
    state.detents.expandedH = Math.round(getVH() * 0.97);
    state.detents.dockGap = dockGap();
    // Start off-screen (slide down), force a reflow so the position commits,
    // then animate up. A forced reflow (not rAF) is used because rAF is paused
    // in background tabs — the slide-up must be reliable.
    setSheet(state.detents.collapsedH, state.detents.dockGap, '110%', false);
    void els.sheet.offsetHeight; // reflow
    snapTo('collapsed', true);
  }

  function close() {
    if (!els.overlay.classList.contains('is-open')) return;
    setSheet(curSheetH(), curSheetBottom(), '110%', true); // slide down off-screen
    els.overlay.classList.remove('is-open');
    els.overlay.setAttribute('aria-hidden', 'true');
    unlockBodyScroll();
    releaseFocusTrap();
    state.expanded = false;
    if (state.lastFocused && state.lastFocused.focus) state.lastFocused.focus();
  }

  // Space reserved below the collapsed card for the floating circle dock.
  function dockGap() {
    return (els.dock && !els.dock.hidden) ? 92 : 16;
  }

  function lockBodyScroll() {
    state.scrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
    document.body.style.top = '-' + state.scrollY + 'px';
    document.documentElement.classList.add('iqv-lock');
  }
  function unlockBodyScroll() {
    document.documentElement.classList.remove('iqv-lock');
    document.body.style.top = '';
    window.scrollTo(0, state.scrollY || 0);
  }

  function getVH() { return window.innerHeight || document.documentElement.clientHeight; }

 function computeDetents() {
  var vh = getVH();
  var grabberH = els.grabber ? els.grabber.offsetHeight : 20;

  /*
   * Measure everything up to and including the price/Add button.
   * This ensures the primary purchase action remains visible.
   */
  var throughAdd = els.buybar
    ? els.buybar.offsetTop + els.buybar.offsetHeight
    : Math.round(vh * 0.68);

  var requestedHeight = grabberH + throughAdd + 24;
  var gap = dockGap();

  /*
   * The popup will open at a minimum of 70% of the screen.
   * It may grow further when the product title or variants need more space.
   */
  var minimumHeight = Math.round(vh * 0.70);
  var maximumHeight = vh - gap - 10;

  minimumHeight = Math.min(minimumHeight, maximumHeight);

  state.detents.dockGap = gap;

  state.detents.collapsedH = Math.round(
    clamp(
      requestedHeight,
      minimumHeight,
      maximumHeight
    )
  );

  state.detents.expandedH = Math.round(vh * 0.98);
}
  // Height-anchored model: the card is bottom-anchored at --iqv-bottom with
  // height --iqv-h; growing the height makes it expand upward (and --iqv-bottom
  // → 0 lets it reach the screen edge). --iqv-slide slides it fully away.
  function setSheet(h, bottom, slide, animate) {
    els.sheet.classList.toggle('iqv-animate', !!animate);
    els.sheet.style.setProperty('--iqv-h', h + 'px');
    els.sheet.style.setProperty('--iqv-bottom', bottom + 'px');
    els.sheet.style.setProperty('--iqv-slide', slide);
  }
  function curSheetH() { return parseFloat(getComputedStyle(els.sheet).getPropertyValue('--iqv-h')) || state.detents.collapsedH; }
  function curSheetBottom() { return parseFloat(getComputedStyle(els.sheet).getPropertyValue('--iqv-bottom')) || 0; }
  function setPagerX(x, animate) {
    els.pager.classList.toggle('iqv-animate', !!animate);
    els.pager.style.transform = x ? 'translateX(' + x + 'px)' : '';
  }

  function snapTo(which, animate) {
    if (which === 'expanded') {
      state.expanded = true;
      els.sheet.classList.add('is-expanded');
      els.sheet.classList.remove('is-collapsed');
      setSheet(state.detents.expandedH, 0, '0', animate);
    } else if (which === 'closed') {
      setSheet(curSheetH(), curSheetBottom(), '110%', animate);
    } else {
      state.expanded = false;
      els.sheet.classList.add('is-collapsed');
      els.sheet.classList.remove('is-expanded');
      els.scroll.scrollTop = 0;
      setSheet(state.detents.collapsedH, state.detents.dockGap, '0', animate);
    }
  }

  function doExpand() {
    if (state.expanded) return;
    snapTo('expanded', true);
    loadDetail(state.product);
  }
  function doCollapse() {
    if (!state.expanded) return;
    els.scroll.scrollTop = 0;
    snapTo('collapsed', true);
  }

  // --------------------------------------------------------------------
  // Gesture engine — vertical sheet drag + horizontal image/product swipe
  // --------------------------------------------------------------------
  function bindGestures() {
    var startX = 0;
    var startY = 0;
    var lastY = 0;
    var lastT = 0;
    var vY = 0;
    var sheetStart = 0;
    var scrollStart = 0;
    var axis = null;
    var mode = null;
    var active = false;
    var canImageSwipe = false;
    var canProductSwipe = false;

    els.sheet.addEventListener('touchstart', function (e) {
      if (e.touches.length !== 1 || els.sheet.classList.contains('iqv-swapping')) return;

      var touch = e.touches[0];
      var target = e.target;

      startX = touch.clientX;
      startY = touch.clientY;
      lastY = touch.clientY;
      lastT = Date.now();
      vY = 0;
      sheetStart = curSheetH();
      scrollStart = els.scroll.scrollTop;
      axis = null;
      mode = null;
      active = true;

      canImageSwipe = !!(
        target.closest &&
        target.closest('.iqv-hero') &&
        imageCount() > 1 &&
        !target.closest('button, a')
      );

      // Product swipe is deliberately limited to the quiet preview surface.
      // Buttons, links, variants and the purchase row retain normal interaction.
      canProductSwipe = !!(
        target.closest &&
        target.closest('.iqv-preview') &&
        !target.closest('button, a, input, select, textarea, .iqv-variants, .iqv-buybar') &&
        state.siblings.length > 1
      );

      els.sheet.classList.remove('iqv-animate');
      els.pager.classList.remove('iqv-animate');
    }, { passive: true });

    els.sheet.addEventListener('touchmove', function (e) {
      if (!active || !e.touches.length) return;

      var touch = e.touches[0];
      var dx = touch.clientX - startX;
      var dy = touch.clientY - startY;
      var now = Date.now();
      var dt = now - lastT;

      if (dt > 0) vY = (touch.clientY - lastY) / dt;
      lastY = touch.clientY;
      lastT = now;

      if (axis === null) {
        if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
        axis = Math.abs(dx) > Math.abs(dy) ? 'h' : 'v';

        if (axis === 'h') {
          if (canImageSwipe) mode = 'image';
          else if (canProductSwipe) mode = 'product';
          else mode = 'native';
        } else if (!state.expanded) {
          mode = 'sheet';
        } else {
          // Expanded content scrolls normally. Pulling down from scrollTop 0
          // hands control back to the sheet so it can collapse.
          mode = (scrollStart <= 0 && dy > 0) ? 'sheet' : 'native';
        }
      }

      if (mode === 'sheet') {
        e.preventDefault();
        dragSheet(dy);
      } else if (mode === 'image') {
        // Prevent browser navigation/scroll while preserving the clean fade
        // transition between photographs.
        e.preventDefault();
      } else if (mode === 'product') {
        e.preventDefault();
        var x = dx;
        if ((state.index <= 0 && dx > 0) ||
            (state.index >= state.siblings.length - 1 && dx < 0)) {
          x = dx * 0.3;
        }
        setPagerX(x, false);
      }
    }, { passive: false });

    function dragSheet(dy) {
      var collapsedH = state.detents.collapsedH;
      var expandedH = state.detents.expandedH;
      var gap = state.detents.dockGap;

      if (!state.expanded && dy > 0) {
        var pct = Math.min((dy / Math.max(1, collapsedH + gap)) * 100, 100);
        setSheet(collapsedH, gap, pct + '%', false);
        return;
      }

      var h = sheetStart - dy;
      h = clamp(h, collapsedH - 20, expandedH + 30);
      var progress = clamp((h - collapsedH) / Math.max(1, expandedH - collapsedH), 0, 1);
      setSheet(h, gap * (1 - progress), '0', false);
    }

    function end(e) {
      if (!active) return;
      active = false;

      var changed = e.changedTouches && e.changedTouches[0];
      var endX = changed ? changed.clientX : startX;
      var endY = changed ? changed.clientY : startY;
      var dx = endX - startX;
      var dy = endY - startY;

      if (mode === 'sheet') finishSheetDrag(dy);
      else if (mode === 'image') finishImageSwipe(dx);
      else if (mode === 'product') finishProductSwipe(dx);

      axis = null;
      mode = null;
    }

    function finishSheetDrag(dy) {
      var vh = getVH();
      var collapsedH = state.detents.collapsedH;
      var expandedH = state.detents.expandedH;
      var range = Math.max(1, expandedH - collapsedH);

      if (!state.expanded && dy > 0) {
        if (dy > vh * 0.14 || vY > 0.6) close();
        else snapTo('collapsed', true);
        return;
      }

      var h = curSheetH();
      if (!state.expanded) {
        if ((h - collapsedH) > range * 0.28 || vY < -0.48) doExpand();
        else snapTo('collapsed', true);
      } else {
        if ((expandedH - h) > range * 0.36 || vY > 0.52) doCollapse();
        else snapTo('expanded', true);
      }
    }

    function finishImageSwipe(dx) {
      var threshold = Math.min(54, Math.max(34, els.hero.offsetWidth * 0.13));
      if (dx <= -threshold) setActiveImage(state.activeImageIndex + 1);
      else if (dx >= threshold) setActiveImage(state.activeImageIndex - 1);
    }

    function finishProductSwipe(dx) {
      var width = els.sheet.offsetWidth;
      var threshold = Math.min(64, width * 0.22);

      if (dx < -threshold && state.index < state.siblings.length - 1) swipeToSibling(1);
      else if (dx > threshold && state.index > 0) swipeToSibling(-1);
      else setPagerX(0, true);
    }

    els.sheet.addEventListener('touchend', end);
    els.sheet.addEventListener('touchcancel', function () {
      active = false;
      axis = null;
      mode = null;
      setPagerX(0, true);
    });

    // Wheel support is useful for tablets with a trackpad or mouse.
    els.scroll.addEventListener('wheel', function (e) {
      if (state.expanded) {
        if (els.scroll.scrollTop <= 0 && e.deltaY < -30) doCollapse();
      } else if (e.deltaY > 24) {
        doExpand();
      }
    }, { passive: true });

    els.grabber.addEventListener('click', function () {
      if (state.expanded) doCollapse();
      else doExpand();
    });
  }

  // --------------------------------------------------------------------
  // Horizontal product swipe (slide out → swap → slide in), keeping state
  // --------------------------------------------------------------------
  function swipeToSibling(dir, explicitTarget) {
    var target = (typeof explicitTarget === 'number') ? explicitTarget : state.index + dir;
    if (target < 0 || target >= state.siblings.length || target === state.index) { setPagerX(0, true); return; }
    var slideDir = target > state.index ? 1 : -1;
    var w = els.sheet.offsetWidth;
    els.sheet.classList.add('iqv-swapping');
    setPagerX(-slideDir * w, true); // current slides out in the swipe direction
    var swapped = false;
    var run = function () {
      if (swapped) return; swapped = true;
      state.index = target;
      state.session++;
      resolveSibling(state.siblings[target], function (data) {
        if (!data) { setPagerX(0, true); els.sheet.classList.remove('iqv-swapping'); return; }
        fillContent(data);
        setPagerX(slideDir * w, false); // place the new one just off the opposite edge (no transition)
        void els.pager.offsetWidth; // reflow so the off-edge position commits
        computeDetents();
        snapTo(state.expanded ? 'expanded' : 'collapsed', true); // keep detent for the new product
        if (state.expanded) loadDetail(data);
        setPagerX(0, true); // slide it in
        els.sheet.classList.remove('iqv-swapping');
      });
    };
    onceTransitionEnd(els.pager, run);
    setTimeout(run, 360); // fallback (e.g. reduced-motion: no transitionend)
  }

  function resolveSibling(sib, cb) {
    if (!sib) return cb(null);
    var key = cleanUrl(sib.url);
    if (cache.has(key)) return cb(cache.get(key));
    if (sib.triggerEl) {
      var d = readEmbeddedProduct(sib.triggerEl);
      if (d) { cache.set(key, d); return cb(d); }
    }
    fetch(productJsonUrl(key), { credentials: 'same-origin' })
      .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
      .then(function (raw) { var d = normalizeAjax(raw); cache.set(key, d); cb(d); })
      .catch(function () { cb(null); });
  }

  // --------------------------------------------------------------------
  // Expanded detail
  // --------------------------------------------------------------------
  function loadDetail(product) {
    if (!product || !els.detailContent) return;

    var key = cleanUrl(product.url);
    if (detailCache.has(key)) {
      injectDetail(detailCache.get(key));
      return;
    }

    els.detailContent.innerHTML = '';
    els.detailContent.hidden = true;
    if (els.detailLoading) els.detailLoading.hidden = false;

    // The default mode uses the Shopify product description already present in
    // product JSON. It is faster and avoids injecting an entire page into a modal.
    if ((config.detailMode || 'description') !== 'page') {
      var detail = buildDescriptionDetail(product);
      detailCache.set(key, detail);
      injectDetail(detail);
      return;
    }

    // Optional legacy mode: set iqvConfig.detailMode = 'page' to fetch the
    // product page. The extracted fragment is aggressively cleaned first.
    var mySession = state.session;
    fetch(product.url, { credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.text();
      })
      .then(function (html) {
        var fragment = extractDetailPage(html);
        detailCache.set(key, fragment);
        if (state.session === mySession && state.product && cleanUrl(state.product.url) === key) {
          injectDetail(fragment);
        }
      })
      .catch(function (err) {
        console.error('[IQV] detail', err);
        injectDetail(buildDescriptionDetail(product));
      });
  }

  function buildDescriptionDetail(product) {
    var heading = escapeHtml(strings.detailsTitle || 'Product details');
    var linkLabel = escapeHtml(strings.fullDetails || 'View full product details');
    var body = sanitizeDescription(product.descriptionHtml || '');

    if (!body && product.shortDescription) {
      body = '<p>' + escapeHtml(product.shortDescription) + '</p>';
    }

    if (!body) {
      body = '<p>' + escapeHtml(strings.noDetails || 'More product information is available on the full product page.') + '</p>';
    }

    return '' +
      '<section class="iqv-inline-detail">' +
        '<h3>' + heading + '</h3>' +
        '<div class="rte iqv-inline-detail__body">' + body + '</div>' +
        '<p><a class="iqv-explore" data-iqv-ignore href="' + escapeAttribute(product.url) + '">' + linkLabel + '</a></p>' +
      '</section>';
  }

  function extractDetailPage(html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var main = doc.querySelector('[data-product-root], .product-template, main, #PageContainer') || doc.body;
    if (!main) return '';

    main.querySelectorAll([
      'script', 'noscript', 'style', 'template',
      'header', 'footer', 'nav',
      '.breadcrumb', '.stickyCart', '#site-scroll', 'cart-drawer',
      '#shopify-section-header', '[id*="section-footer"]',
      '[id*="mobile-toolbar"]', '.exitprPopup',
      '.hov-media', 'form[action*="/cart/add"]'
    ].join(',')).forEach(function (node) { node.remove(); });

    main.querySelectorAll('[id]').forEach(function (node) { node.removeAttribute('id'); });
    main.querySelectorAll('img[data-src]').forEach(function (img) {
      img.src = img.getAttribute('data-src');
      img.removeAttribute('loading');
    });
    main.querySelectorAll('img[data-srcset]').forEach(function (img) {
      img.srcset = img.getAttribute('data-srcset');
    });
    main.querySelectorAll('source[data-srcset]').forEach(function (source) {
      source.srcset = source.getAttribute('data-srcset');
    });

    return main.innerHTML;
  }

  function injectDetail(html) {
    if (els.detailLoading) els.detailLoading.hidden = true;
    els.detailContent.innerHTML = html;
    els.detailContent.hidden = false;

    if (window.lazySizes && window.lazySizes.loader && typeof window.lazySizes.loader.checkElems === 'function') {
      try { window.lazySizes.loader.checkElems(); } catch (e) {}
    }
  }

  // --------------------------------------------------------------------
  // Fill the preview content (shared by fresh-open and swipe)
  // --------------------------------------------------------------------
  function fillContent(data) {
    state.product = data;
    state.activeVariantId = data.selectedVariantId || (data.variants[0] && data.variants[0].id);

    els.addBtn.classList.remove('is-loading', 'is-added');
    els.detailContent.innerHTML = '';
    els.detailContent.hidden = true;
    els.detailLoading.hidden = true;

    renderHeroImages(data);
    renderDots(data);
    renderProductDock(data, state.siblings);

    var ratingValue = data.rating || fallback.rating || '4.6';
    if (els.rating) els.rating.textContent = '★ ' + ratingValue;
    if (els.reviewEyebrow) els.reviewEyebrow.textContent = strings.reviewEyebrow || 'CUSTOMER RATING';
    if (els.reviewLabel) els.reviewLabel.textContent = strings.reviewLabel || 'Based on customer reviews';
    if (els.reviewArrow) els.reviewArrow.textContent = '›';
    renderWishlistState(data);
    renderExplore(data);

    els.title.textContent = data.title;
    toggleText(els.subtitle, data.shortDescription);
    toggleText(els.qty, data.quantityText);

    renderVariants(data);
    renderPriceForVariant(getActiveVariant());
    toggleOffer(data.offerText);

    if (config.multiCurrency && typeof window.currenciesChange === 'function') {
      window.currenciesChange('#InstamartQuickViewRoot .money');
    }
  }

  // -- hero / dots -----------------------------------------------------
  function renderHeroImages(data) {
    els.heroTrack.innerHTML = '';
    state.activeImageIndex = 0;

    var images = Array.isArray(data.images) ? data.images : [];
    els.hero.classList.toggle('has-no-image', images.length === 0);

    images.forEach(function (img, index) {
      var image = document.createElement('img');
      image.className = 'iqv-hero__slide' + (index === 0 ? ' is-active' : '');
      image.src = img.large || img.src || '';
      image.alt = img.alt || data.title || '';
      image.loading = index === 0 ? 'eager' : 'lazy';
      image.decoding = 'async';
      image.draggable = false;

      if (index === 0) {
        image.addEventListener('load', function () {
          if (!els.overlay.classList.contains('is-open')) return;
          computeDetents();
          snapTo(state.expanded ? 'expanded' : 'collapsed', false);
        }, { once: true });
      }

      els.heroTrack.appendChild(image);
    });
  }

  function renderDots(data) {
    els.dots.innerHTML = '';
    var count = Array.isArray(data.images) ? data.images.length : 0;

    if (count <= 1) {
      els.dots.hidden = true;
      return;
    }

    els.dots.hidden = false;
    for (var i = 0; i < count; i++) {
      var dot = document.createElement('span');
      dot.className = 'iqv-dot' + (i === 0 ? ' is-active' : '');
      dot.setAttribute('role', 'button');
      dot.setAttribute('tabindex', '0');
      dot.setAttribute('aria-label', 'Show product image ' + (i + 1));
      dot.addEventListener('click', makeImgHandler(i));
      dot.addEventListener('keydown', makeDotKeyHandler(i));
      els.dots.appendChild(dot);
    }
  }

  function makeImgHandler(index) {
    return function () { setActiveImage(index); };
  }

  function makeDotKeyHandler(index) {
    return function (event) {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        setActiveImage(index);
      }
    };
  }

  function setActiveImage(index) {
    var slides = els.heroTrack.querySelectorAll('.iqv-hero__slide');
    if (!slides.length) return;

    index = ((index % slides.length) + slides.length) % slides.length;
    state.activeImageIndex = index;

    slides.forEach(function (slide, i) {
      slide.classList.toggle('is-active', i === index);
      slide.setAttribute('aria-hidden', i === index ? 'false' : 'true');
    });

    els.dots.querySelectorAll('.iqv-dot').forEach(function (dot, i) {
      dot.classList.toggle('is-active', i === index);
      dot.setAttribute('aria-current', i === index ? 'true' : 'false');
    });
  }

  function imageCount() {
    return els.heroTrack ? els.heroTrack.querySelectorAll('.iqv-hero__slide').length : 0;
  }

  // -- circular dock (other products) ---------------------------------
  function renderProductDock(data, siblings) {
    els.thumbRail.innerHTML = '';
    if (!siblings || siblings.length <= 1) { els.dock.hidden = true; return; }
    els.dock.hidden = false;
    var active = urlHandle(data.url);
    siblings.forEach(function (sib) {
      var isActive = urlHandle(sib.url) === active;
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'iqv-thumb' + (isActive ? ' is-active' : '');
      btn.setAttribute('aria-label', sib.title ? 'View ' + sib.title : 'View product');
      if (sib.thumb) {
        var im = document.createElement('img');
        im.src = sib.thumb; im.alt = ''; im.loading = 'lazy'; im.decoding = 'async';
        btn.appendChild(im);
      }
      btn.addEventListener('click', function () {
        if (isActive) return;
        swipeToSibling(0, indexOfHandle(siblings, urlHandle(sib.url)));
      });
      els.thumbRail.appendChild(btn);
      if (isActive) setTimeout(function () { btn.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' }); }, 40);
    });
  }

  // -- variants / price ------------------------------------------------
  function renderVariants(data) {
    els.variantsRail.innerHTML = '';
    if (!data.hasVariants || data.variants.length <= 1) { els.variantsWrap.hidden = true; return; }
    els.variantsWrap.hidden = false;
    data.variants.forEach(function (v) {
      var btn = document.createElement('button');
      btn.type = 'button';
      var c = ['iqv-variant-card'];
      if (v.id === state.activeVariantId) c.push('is-selected');
      if (!v.available) c.push('is-soldout');
      btn.className = c.join(' ');
      btn.disabled = !v.available;
      var t = document.createElement('span'); t.className = 'iqv-variant-card__title'; t.textContent = v.title;
      var p = document.createElement('span'); p.className = 'iqv-variant-card__price money'; p.textContent = v.priceFormatted;
      btn.appendChild(t); btn.appendChild(p);
      btn.addEventListener('click', function () { selectVariant(v.id); });
      els.variantsRail.appendChild(btn);
    });
  }
  function selectVariant(id) {
    if (!state.product) return;
    state.activeVariantId = id;
    els.variantsRail.querySelectorAll('.iqv-variant-card').forEach(function (card, i) {
      card.classList.toggle('is-selected', state.product.variants[i].id === id);
    });
    var v = getActiveVariant();
    if (v && v.imageId) {
      var idx = state.product.images.findIndex(function (img) { return img.id === v.imageId; });
      if (idx > -1) setActiveImage(idx);
    }
    renderPriceForVariant(v);
  }
  function getActiveVariant() {
    if (!state.product) return null;
    var vs = state.product.variants;
    return vs.filter(function (v) { return v.id === state.activeVariantId; })[0] || vs[0] || null;
  }
  function renderPriceForVariant(v) {
    if (!v) return;
    els.price.textContent = v.priceFormatted;
    var showCompare = v.hasDiscount && v.compareAtPriceFormatted;
    if (showCompare) { els.comparePrice.textContent = v.compareAtPriceFormatted; els.comparePrice.hidden = false; }
    else els.comparePrice.hidden = true;
    if (showCompare && v.discountPercent > 0) { els.discount.textContent = v.discountPercent + '% OFF'; els.discount.hidden = false; }
    else els.discount.hidden = true;
    toggleText(els.unitPrice, v.unitPriceText);
    var soldOut = !v.available;
    els.addBtn.disabled = soldOut;
    els.addBtn.classList.toggle('is-soldout', soldOut);
    els.addBtn.classList.remove('is-added', 'is-loading');
    els.addLabel.textContent = soldOut ? (strings.soldOut || 'Sold Out') : (strings.add || 'ADD');
  }

  function renderExplore(data) {
    if (data.explore && data.explore.url) {
      els.explore.href = data.explore.url;
      els.exploreText.textContent = (strings.exploreAllPrefix || 'Explore all') + ' ' + data.explore.title + ' ' + (strings.exploreAllSuffix || 'items');
      els.explore.hidden = false;
    } else els.explore.hidden = true;
  }
  function renderWishlistState(data) {
    if (!els.wishlistBtn) return;
    els.wishlistBtn.setAttribute('rel', data.handle || '');
    els.wishlistBtn.classList.toggle('addto-wishlist', !isWishlisted(data.handle));
  }
  function isWishlisted(handle) {
    if (!handle) return false;
    try { var s = window.localStorage.getItem('wishlist'); return !!s && s.split('+').indexOf(handle) !== -1; } catch (e) { return false; }
  }
  function toggleText(el, text) {
    if (!el) return;
    if (text) { el.textContent = text; el.hidden = false; }
    else el.hidden = true;
  }
  function toggleOffer(text) {
    if (!els.offer || !els.offerCard) return;
    if (text) { els.offer.textContent = text; els.offerCard.hidden = false; }
    else els.offerCard.hidden = true;
  }

  function onShareClick() {
    var d = state.product; if (!d) return;
    var url = window.location.origin + d.url;
    if (navigator.share) { navigator.share({ title: d.title, url: url }).catch(function () {}); return; }
    if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(url);
  }

  // --------------------------------------------------------------------
  // Add to cart
  // --------------------------------------------------------------------
  function onAddClick() {
    if (!state.product || els.addBtn.disabled || els.addBtn.classList.contains('is-loading')) return;
    var variant = getActiveVariant();
    if (!variant || !variant.available) return;
    var mySession = state.session;
    els.addBtn.classList.add('is-loading');
    els.addBtn.disabled = true;

    var cartDrawer = document.querySelector('cart-drawer');
    var canDrawer = !!(cartDrawer && typeof cartDrawer.renderContents === 'function' && typeof cartDrawer.getSectionsToRender === 'function');
    var body = new FormData();
    body.append('id', variant.id);
    body.append('quantity', 1);
    if (canDrawer) {
      cartDrawer.getSectionsToRender().forEach(function (s) { body.append('sections', s.id); });
      body.append('sections_url', window.location.pathname);
    }
    fetch(CART_ADD_URL, { method: 'POST', headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' }, body: body })
      .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, json: j }; }); })
      .then(function (res) {
        if (!res.ok || res.json.status) throw new Error((res.json && (res.json.description || res.json.message)) || 'add failed');
        refreshCartCount();
        if (canDrawer) cartDrawer.renderContents(res.json);
        if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
          window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'instamart-quick-view', productVariantId: variant.id, cartData: res.json });
        }
        if (state.session === mySession) showAdded(mySession);
      })
      .catch(function (err) {
        console.error('[IQV]', err);
        if (state.session === mySession) { els.addBtn.classList.remove('is-loading'); els.addBtn.disabled = false; }
      });
  }
  function showAdded(session) {
    els.addBtn.classList.remove('is-loading');
    els.addBtn.classList.add('is-added');
    els.addLabel.textContent = strings.added || 'ADDED';
    window.setTimeout(function () {
      if (state.session !== session) return;
      els.addBtn.classList.remove('is-added');
      els.addBtn.disabled = false;
      var v = getActiveVariant();
      els.addLabel.textContent = v && v.available ? (strings.add || 'ADD') : (strings.soldOut || 'Sold Out');
    }, ADDED_RESET_MS);
  }
  function refreshCartCount() {
    if (typeof window.freeShippMsg === 'function') { window.freeShippMsg(); return; }
    fetch(CART_URL, { credentials: 'same-origin' }).then(function (r) { return r.json(); }).then(function (cart) {
      document.querySelectorAll('.cart-count, .cart-count-bubble span, [data-cart-count], #CartCount').forEach(function (el) { el.textContent = cart.item_count; });
    }).catch(function () {});
  }

  // --------------------------------------------------------------------
  // Loading / error state
  // --------------------------------------------------------------------
  function setLoading(on) { els.sheet.classList.toggle('is-loading', on); }
  function setError(on) { els.sheet.classList.toggle('has-error', on); }

  // --------------------------------------------------------------------
  // Focus trap
  // --------------------------------------------------------------------
  function activateFocusTrap() {
    if (typeof window.trapFocus === 'function') window.trapFocus(els.sheet, els.sheet);
    else { document.addEventListener('keydown', localTabTrap); }
  }
  function releaseFocusTrap() {
    if (typeof window.removeTrapFocus === 'function') window.removeTrapFocus(state.lastFocused);
    else document.removeEventListener('keydown', localTabTrap);
  }
  function localTabTrap(e) {
    if (e.key !== 'Tab') return;
    var f = els.sheet.querySelectorAll('button:not([disabled]), a[href], input, [tabindex]:not([tabindex="-1"])');
    if (!f.length) return;
    var first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  // --------------------------------------------------------------------
  // Siblings — build the browsable set from the grid the shopper opened from
  // --------------------------------------------------------------------
  function buildSiblings(triggerEl) {
    var scope = findSiblingScope(triggerEl);
    var sel = '[data-iqv-trigger]' + (getAutoBindSelector() ? ', ' + getAutoBindSelector() : '');
    var nodes = Array.prototype.slice.call(scope.querySelectorAll(sel));
    var seen = {}, out = [];
    nodes.forEach(function (el) {
      if (el.closest('[data-iqv-ignore]')) return;
      var isTrigger = el.hasAttribute('data-iqv-trigger');
      var url = el.getAttribute('href');
      if (!url || url.charAt(0) === '#') return;
      var handle = urlHandle(url);
      if (!handle || seen[handle]) return;
      var card = el.closest('.grid-view-item, .list-view-item, [data-iqv-trigger]') || el;
      var thumb = ensureSmallThumb(extractThumbFromCard(card));
      var title = isTrigger ? (el.getAttribute('aria-label') || '') : ((card.querySelector('.grid-view-item__title, .list-view-item__title, .grid-view-item__link') || {}).textContent || '');
      seen[handle] = true;
      out.push({ url: url, thumb: thumb, title: title.trim(), triggerEl: isTrigger ? el : null });
    });
    return out;
  }
  function findSiblingScope(triggerEl) {
    if (!triggerEl || typeof triggerEl.closest !== 'function') return document;
    var node = triggerEl.closest('.gitem, .grid-view-item, .list-view-item, [data-iqv-trigger]') || triggerEl;
    var scope = node.parentElement, hops = 0;
    while (scope && scope !== document.body && hops < 4) {
      if (countUnique(scope) >= 2) return scope;
      scope = scope.parentElement; hops++;
    }
    return document;
  }
  function countUnique(scope) {
    var sel = '[data-iqv-trigger]' + (getAutoBindSelector() ? ', ' + getAutoBindSelector() : '');
    var nodes = scope.querySelectorAll(sel), h = {}, n = 0;
    for (var i = 0; i < nodes.length; i++) {
      var k = urlHandle(nodes[i].getAttribute('href'));
      if (k && !h[k]) { h[k] = true; n++; }
    }
    return n;
  }
  function indexOfHandle(list, handle) {
    for (var i = 0; i < list.length; i++) if (urlHandle(list[i].url) === handle) return i;
    return -1;
  }
  function ensureSmallThumb(url) { if (!url) return url; return /[?&]width=/.test(url) ? url : resizeShopifyImage(url, 100); }
  function extractThumbFromCard(card) {
    if (!card) return null;
    var img = card.tagName === 'IMG' ? card : card.querySelector('img');
    if (img) {
      if (img.currentSrc && img.currentSrc.indexOf('data:') !== 0) return img.currentSrc;
      if (img.dataset.src) return img.dataset.src;
      if (img.dataset.srcset) { var a = firstUrlFromSrcset(img.dataset.srcset); if (a) return a; }
      if (img.srcset) { var b = firstUrlFromSrcset(img.srcset); if (b) return b; }
      if (img.src && img.src.indexOf('data:') !== 0) return img.src;
    }
    var bg = card.hasAttribute('data-bgset') ? card : card.querySelector('[data-bgset]');
    if (bg) { var c = firstUrlFromBgset(bg.getAttribute('data-bgset')); if (c) return c; }
    var st = card.querySelector('[style*="background-image"]');
    if (st) { var m = /url\((['"]?)(.*?)\1\)/.exec(st.style.backgroundImage); if (m) return m[2]; }
    return null;
  }
  function firstUrlFromSrcset(s) { if (!s) return null; var f = s.split(',')[0].trim(); return f.split(/\s+/)[0] || null; }
  function firstUrlFromBgset(s) { if (!s) return null; var p = s.split(',').map(function (x) { return x.trim(); }).filter(Boolean); if (!p.length) return null; return p[p.length - 1].split(/\s+/)[0] || null; }

  // --------------------------------------------------------------------
  // Normalization
  // --------------------------------------------------------------------
  function normalizeEmbedded(raw) {
    raw = raw || {};
    var images = (raw.images || []).map(function (image, index) {
      return normalizeImage(image, index, raw.title);
    }).filter(Boolean);

    var variants = (raw.variants || []).map(function (variant) {
      return normalizeVariant(variant, images);
    });

    var selected = raw.selectedVariantId || raw.selected_variant_id || (variants[0] && variants[0].id);
    return {
      id: raw.id,
      handle: raw.handle || urlHandle(raw.url),
      url: raw.url || (raw.handle ? '/products/' + raw.handle : ''),
      title: raw.title || '',
      vendor: raw.vendor || '',
      available: raw.available !== false,
      shortDescription: raw.shortDescription || raw.short_description || truncate(stripHtml(raw.descriptionHtml || raw.description || ''), 140),
      descriptionHtml: raw.descriptionHtml || raw.description_html || raw.description || '',
      quantityText: raw.quantityText || raw.quantity_text || fallback.quantityText || '1 Piece',
      rating: raw.rating || fallback.rating || '4.6',
      offerText: raw.offerText || raw.offer_text || fallback.offerText || '',
      explore: raw.explore || null,
      selectedVariantId: selected,
      hasVariants: typeof raw.hasVariants === 'boolean' ? raw.hasVariants : hasRealVariants(variants),
      images: images,
      variants: variants
    };
  }

  function normalizeAjax(raw) {
    raw = raw || {};
    var rawImages = raw.images || [];
    var images = rawImages.map(function (image, index) {
      return normalizeImage(image, index, raw.title);
    }).filter(Boolean);

    var variants = (raw.variants || []).map(function (variant) {
      return normalizeVariant(variant, images);
    });

    var firstTitle = raw.variants && raw.variants[0] && raw.variants[0].title;
    return {
      id: raw.id,
      handle: raw.handle,
      url: raw.url || '/products/' + raw.handle,
      title: raw.title || '',
      vendor: raw.vendor || '',
      available: raw.available !== false,
      shortDescription: truncate(stripHtml(raw.description), 140),
      descriptionHtml: raw.description || '',
      quantityText: firstTitle && firstTitle !== 'Default Title' ? firstTitle : (fallback.quantityText || '1 Piece'),
      rating: fallback.rating || '4.6',
      offerText: fallback.offerText || '',
      explore: buildExploreFallback(raw),
      selectedVariantId: variants[0] && variants[0].id,
      hasVariants: hasRealVariants(variants),
      images: images,
      variants: variants
    };
  }

  function normalizeImage(image, index, fallbackAlt) {
    var src = '';
    var alt = fallbackAlt || '';
    var id = 'src' + index;

    if (typeof image === 'string') {
      src = image;
    } else if (image && typeof image === 'object') {
      src = image.large || image.src || image.url || image.original || '';
      alt = image.alt || alt;
      id = image.id || id;
    }

    if (!src) return null;
    return {
      id: String(id),
      src: src,
      large: resizeShopifyImage(src, 1200),
      thumb: resizeShopifyImage(src, 220),
      alt: alt
    };
  }

  function normalizeVariant(variant, images) {
    variant = variant || {};
    var price = numberOrZero(variant.price);
    var compare = numberOrZero(variant.compare_at_price != null ? variant.compare_at_price : variant.compareAtPrice);
    var hasDiscount = typeof variant.hasDiscount === 'boolean'
      ? variant.hasDiscount
      : compare > price && compare > 0;

    var featuredSrc = variant.featured_image && (variant.featured_image.src || variant.featured_image.url);
    var imageId = variant.imageId || variant.image_id || '';

    if (!imageId && featuredSrc) {
      var normalizedFeatured = cleanImageUrl(featuredSrc);
      var match = images.filter(function (image) {
        return cleanImageUrl(image.src || image.large) === normalizedFeatured;
      })[0];
      if (match) imageId = match.id;
    }

    return {
      id: variant.id,
      title: variant.title || 'Default Title',
      available: variant.available !== false,
      priceFormatted: variant.priceFormatted || variant.price_formatted || formatMoney(price),
      compareAtPriceFormatted: variant.compareAtPriceFormatted || variant.compare_at_price_formatted || (hasDiscount ? formatMoney(compare) : ''),
      hasDiscount: hasDiscount,
      discountPercent: variant.discountPercent != null
        ? Number(variant.discountPercent) || 0
        : (hasDiscount ? Math.round(((compare - price) / compare) * 100) : 0),
      imageId: imageId,
      unitPriceText: variant.unitPriceText || variant.unit_price_text || ''
    };
  }

  function hasRealVariants(variants) {
    if (!variants || variants.length <= 1) return false;
    return !(variants.length === 1 && variants[0].title === 'Default Title');
  }

  function buildExploreFallback(raw) {
    var collection = config.currentCollection;
    if (collection && collection.url) return { url: collection.url, title: collection.title };
    if (raw.vendor) {
      return {
        url: '/search?q=' + encodeURIComponent('vendor:' + raw.vendor) + '&type=product',
        title: raw.vendor
      };
    }
    return null;
  }

  // --------------------------------------------------------------------
  // Helpers
  // --------------------------------------------------------------------
  function cleanUrl(h) { return String(h || '').split('?')[0].split('#')[0]; }
  function productJsonUrl(url) {
    var cleaned = cleanUrl(url);
    return /\.js$/i.test(cleaned) ? cleaned : cleaned + '.js';
  }
  function urlHandle(h) {
    if (!h) return '';
    var parts = cleanUrl(h).split('/').filter(Boolean);
    var handle = parts[parts.length - 1] || '';
    return handle.replace(/\.js$/, '');
  }
  function resizeShopifyImage(url, width) {
    if (!url) return url;
    var value = String(url);
    if (/[?&]width=\d+/i.test(value)) return value.replace(/([?&]width=)\d+/i, '$1' + width);
    return value + (value.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }
  function cleanImageUrl(url) {
    return String(url || '').split('?')[0].replace(/^https?:/, '');
  }
  function stripHtml(html) {
    var div = document.createElement('div');
    div.innerHTML = html || '';
    return (div.textContent || '').replace(/\s+/g, ' ').trim();
  }
  function sanitizeDescription(html) {
    if (!html) return '';
    var doc = new DOMParser().parseFromString('<div>' + html + '</div>', 'text/html');
    var root = doc.body.firstElementChild;
    if (!root) return '';
    root.querySelectorAll('script, style, iframe, object, embed, form, input, button, noscript').forEach(function (node) { node.remove(); });
    root.querySelectorAll('*').forEach(function (node) {
      Array.prototype.slice.call(node.attributes).forEach(function (attribute) {
        if (/^on/i.test(attribute.name)) node.removeAttribute(attribute.name);
      });
    });
    return root.innerHTML;
  }
  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }
  function escapeAttribute(value) { return escapeHtml(value); }
  function truncate(value, length) {
    if (!value || value.length <= length) return value || '';
    return value.slice(0, Math.max(0, length - 1)) + '…';
  }
  function numberOrZero(value) {
    if (typeof value === 'number') return value;
    var parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  function clamp(value, min, max) { return Math.max(min, Math.min(max, value)); }
  function debounce(fn, ms) {
    var timer;
    return function () {
      var args = arguments;
      var context = this;
      clearTimeout(timer);
      timer = setTimeout(function () { fn.apply(context, args); }, ms);
    };
  }
  function onceTransitionEnd(el, fn) {
    var done = false;
    function h(e) { if (e && e.target !== el) return; if (done) return; done = true; el.removeEventListener('transitionend', h); fn(); }
    el.addEventListener('transitionend', h);
  }
  function formatMoney(cents) {
    if (typeof cents !== 'number') cents = parseFloat(cents) || 0;
    var re = /\{\{\s*(\w+)\s*\}\}/, m = MONEY_FORMAT.match(re), ph = m ? m[1] : 'amount', val;
    switch (ph) {
      case 'amount_no_decimals': val = withDelim(cents, 0, '.', ','); break;
      case 'amount_with_comma_separator': val = withDelim(cents, 2, ',', '.'); break;
      case 'amount_no_decimals_with_comma_separator': val = withDelim(cents, 0, ',', '.'); break;
      default: val = withDelim(cents, 2, '.', ',');
    }
    return MONEY_FORMAT.replace(re, val);
  }
  function withDelim(cents, prec, dec, thou) {
    var v = (cents / 100).toFixed(prec), parts = v.split('.'), d = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thou);
    return parts[1] ? d + dec + parts[1] : d;
  }
})();